<i18n src="@/locales/modules/UserSettingsTranslate.json"></i18n>

<template>
	<div>
		<div v-for="(grade, year) in localPoints" :key="year" class="bg-gray-500 p-1 my-4">
			<div class="bg-white p-4 border rounded-b">
				<h2 class="text-lg font-semibold">{{ $t('ProfileDormitory.PointYear') + ' ' + (year + 1) }}</h2>
				<div v-for="(point, semester) in grade" :key="semester" class="semester-config sm:p-4 p-2 sm:mb-4 mb-2">
					<div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2">
						<div class="flex flex-col gap-3">
							<div :class="point.is_current ? 'text-solid-primary text-lg font-black' : 'text-md font-semibold'">
								{{ $t('ProfileDormitory.PointSemester') + ' ' + (semester + 1) }}
								<i v-if="point.confirmed" class="nucleo nucleo-c-check text-solid-green-1 ml-1" />
							</div>
							<vs-checkbox
								v-if="!point.confirmed && (semester === 0 || semester === 1)"
								class="my-4"
								v-model="localPoints[year][semester].is_current"
								@change="changeCurrentSemester(point.id)"
							>
								{{ $t('ProfileDormitory.CurrentSemester') }}
							</vs-checkbox>
							<label for class="vs-input--label">
								{{ $t('ProfileDormitory.CoursePoint') }}:
								<template v-if="point.confirmed">
									<strong>{{ point.course_point }}</strong>
								</template>
							</label>
							<template v-if="!point.confirmed">
								<vs-input
									type="number"
									:placeholder="$t('ProfileDormitory.CoursePoint')"
									:disabled="localPoints[year][semester].confirmed"
									v-model="localPoints[year][semester].course_point"
									class="w-full"
									:name="'course_point_' + year + '_' + semester"
									:data-vv-as="$t('ProfileDormitory.CoursePoint')"
									v-validate="'required|between:0,10'"
								/>
								<span class="text-danger text-sm" v-show="errors.has('course_point_' + year + '_' + semester)">
									{{ errors.first('course_point_' + year + '_' + semester) }}
								</span>
							</template>

							<label for class="vs-input--label">
								{{ $t('ProfileDormitory.SchoolTrainingPoint') }}:
								<template v-if="point.confirmed">
									<strong>{{ point.school_training_point }}</strong>
								</template>
							</label>
							<template v-if="!point.confirmed">
								<vs-input
									type="number"
									:placeholder="$t('ProfileDormitory.SchoolTrainingPoint')"
									:disabled="localPoints[year][semester].confirmed"
									v-model="localPoints[year][semester].school_training_point"
									class="w-full"
									:name="'school_training_point_' + year + '_' + semester"
									:data-vv-as="$t('ProfileDormitory.SchoolTrainingPoint')"
									v-validate="'required|between:0,100'"
								/>
								<span class="text-danger text-sm" v-show="errors.has('school_training_point_' + year + '_' + semester)">
									{{ errors.first('school_training_point_' + year + '_' + semester) }}
								</span>
							</template>

							<label class="vs-input--label">{{ $t('ProfileDormitory.DormitoryTrainingPoint') }}: <strong>{{ point.dormitory_training_point }}</strong>
								<i class="nucleo nucleo-ic_info_24px ml-1 cursor-pointer" @click="toggleShowPointDetail(year, semester)"></i>
							</label>

							<small v-if="point.created_at || point.updated_at">
								{{ $t('ProfileDormitory.LastUpdateAt') }} {{ point.updated_at || point.created_at | dateTime(userDateTimeFormat) }}
							</small>
						</div>
						<div>
							<div class="text-md font-semibold mb-3">{{ $t('ProfileDormitory.Attachments') }}:</div>
							<cbs-upload-ref v-model="localPoints[year][semester].attachs" :isEditable="!localPoints[year][semester].confirmed" />
						</div>
					</div>
					<div v-if="point.isShowPointDetail">
						<div class="p-2 md:p-4" style="background: #fff">
							<table class="events">
								<thead>
									<tr>
										<th class="text-base">Sự kiện</th>
										<th class="text-base">{{ $t('ProfileDormitory.Note') }}</th>
										<th class="text-base">{{ $t('ProfileDormitory.Point') }}</th>
									</tr>
								</thead>
								<tbody v-if="point.point_events && point.point_events.length > 0">
									<tr
										v-for="(event, index) in point.point_events"
										:key="index"
									>
										<td>{{ event.point_event.name }}</td>
										<td>{{ event.notes ? event.notes : $t('ProfileDormitory.NoNote') }}</td>
										<td>{{ (event.point_event.point > 0 ? "+" : '') + event.point_event.point + " " + $t('ProfileDormitory.Point').toLowerCase() }}</td>
									</tr>
								</tbody>
								<tbody v-if="point.point_events.length === 0">
									<tr>
										<td colspan="3">{{ $t('ProfileDormitory.NoEventsYet') }}</td>
									</tr>
								</tbody>
							</table>
							<hr class="my-3 md:my-5">
							<table class="events">
								<thead>
									<tr>
										<th class="text-base">Vi phạm</th>
										<th class="text-base">{{ $t('ProfileDormitory.Note') }}</th>
										<th class="text-base">{{ $t('ProfileDormitory.Point') }}</th>
									</tr>
								</thead>
								<tbody v-if="point.point_events_violation && point.point_events_violation.length > 0">
									<tr
										v-for="(event, index) in point.point_events_violation"
										:key="index"
									>
										<td>{{ event.point_event.name }}</td>
										<td>{{ event.notes ? event.notes : $t('ProfileDormitory.NoNote') }}</td>
										<td>{{ (event.point_event.point > 0 ? "+" : '') + event.point_event.point + " " + $t('ProfileDormitory.Point').toLowerCase() }}</td>
									</tr>
								</tbody>
								<tbody v-if="point.point_events_violation.length === 0">
									<tr>
										<td colspan="3">{{ $t('ProfileDormitory.NoViolationsYet') }}</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
				</div>
				<!-- Nút thêm học kỳ nếu số lượng học kỳ dưới 3 -->
				<div v-if="localPoints[year].length < 3" class="flex flex-wrap items-center justify-center">
					<vs-button class="w-full sm:w-2/3 sm:max-w-xs mt-4 font-bold" @click="addSemester(year)">{{ $t('ProfileDormitory.AddSemester') + (localPoints[year].length + 1) }}</vs-button>
				</div>
			</div>
		</div>
		<!-- Nút thêm năm học nếu số lượng năm học dưới 6 -->
		<div v-if="localPoints.length < 6" class="flex flex-wrap items-center justify-center">
			<vs-button class="w-full sm:w-2/3 sm:max-w-xs mt-4 font-bold" @click="addYear()">{{ $t('ProfileDormitory.AddYear') + (localPoints.length + 1) }}</vs-button>
		</div>
		<div class="flex flex-wrap items-center justify-center">
			<vs-button class="w-full sm:w-1/3 sm:max-w-xs mt-4 font-bold" @click="updateEmployeePoints()">{{ $t('Actions.SaveChanges') }}</vs-button>
		</div>
	</div>
</template>

<script>
import CbsUploadRef from "@/components/cbs-upload-ref/CbsUploadRef";

export default {
	components: {
		CbsUploadRef,
	},
	data() {
		return {
			initPoint: {
				course_point: null,
				school_training_point: null,
				dormitory_training_point: null,
				files: [],
				confirmed: false,
				isShowPointDetail: false,
				point_events: [],
				is_current: false,
			},
			localPoints: this.$store.state.AppActiveUser.role.employee_points.length === 0
				? [[{ ...this.initPoint }]] : this.reverseTransformPoints(this.$store.state.AppActiveUser.role.employee_points),
		};
	},
	methods: {
		addYear() {
			this.localPoints.push([[{ ...this.initPoint }]]);
		},
		addSemester(year) {
			this.localPoints[year].push({ ...this.initPoint });
		},
		deleteSemester(year, semester) {
			this.localPoints[year].splice(semester, 1);
			if (this.localPoints[year].length === 0) {
				this.localPoints.splice(year, 1);
			}
		},
		transformPoints(points) {
			return points.flatMap((yearPoints, i) =>
				yearPoints.map((point, j) => ({
					...point,
					school_year: i + 1,
					semester: j + 1,
					files: point.attachs ? point.attachs.map(a => a.id) : []
				}))
			);
		},
		reverseTransformPoints(data) {
			const sortedData = data.slice().sort((a, b) =>
				a.school_year === b.school_year ? a.semester - b.semester : a.school_year - b.school_year
			);

			const result = [];
			sortedData.forEach(point => {
				const yearIndex = point.school_year - 1;
				if (!result[yearIndex]) {
					result[yearIndex] = [];
				}
				result[yearIndex].push({
					...point,
					isShowPointDetail: false,
				});
			});
			return result;
		},
		updateEmployeePoints() {
			this.$validator
				.validateAll()
				.then(result => {
					if (result) {
						const payload = this.transformPoints(this.localPoints);
						this.$store.dispatch("auth/updateEmployeePoints", JSON.stringify(payload)).then(res => {
							const notifyOptions = {
								context: this,
								options: {
									title: this.$t('ProfileDormitory.UpdatePoints'),
									text: res.employee_points ? this.$t('ProfileDormitory.UpdatePointsSuccessful') : this.$t('ProfileDormitory.UpdatePointsFailed')
								}
							};
							res.employee_points ? this.$helper.notifySuccess(notifyOptions) : this.$helper.notifyError(notifyOptions);
						});
					}
				})
				.catch(() => {
					this.$helper.notifyError({
						context: this,
						options: {
							title: this.$t('ProfileDormitory.UpdatePoints'),
							text: this.$t('ProfileDormitory.UpdatePointsFailed')
						}
					});
				});
		},
		haveIdenticalIds(array1, array2) {
			const ids1 = array1.map(item => item.id).sort();
			const ids2 = array2.map(item => item.id).sort();

			if (ids1.length !== ids2.length) {
				return false;
			}

			for (let i = 0; i < ids1.length; i++) {
				if (ids1[i] !== ids2[i]) {
					return false;
				}
			}

			return true;
		},
		toggleShowPointDetail(year, semester) {
			this.localPoints = this.localPoints.map((yearPoints, i) =>
				yearPoints.map((point, j) => {
					if (i === year && j === semester) {
						return {
							...point, isShowPointDetail: !point.isShowPointDetail,
							point_events: this.$store.state.AppActiveUser.role.point_events.filter(
								(event) => event.school_year === point.school_year && event.semester === point.semester && event.point_event.point > 0
							),
							point_events_violation: this.$store.state.AppActiveUser.role.point_events.filter(
								(event) => event.school_year === point.school_year && event.semester === point.semester && event.point_event.point < 0
							),
						};
					}
					return point;
				})
			);
		},
		changeCurrentSemester(id) {
			// Tìm học kỳ được chọn dựa trên ID
			let selectedSemester = null;
			this.localPoints.forEach((yearPoints) => {
				yearPoints.forEach((point) => {
					if (point.id === id) {
						selectedSemester = point;
					}
				});
			});

			// Nếu học kỳ được chọn đã có `is_current = true`, không thay đổi gì cả
			if (selectedSemester && !selectedSemester.is_current) {
				return;
			}

			// Ngược lại, cập nhật: đặt học kỳ được chọn thành `true`, các học kỳ khác thành `false`
			this.localPoints = this.localPoints.map((yearPoints) => {
				return yearPoints.map((point) => {
					// Nếu học kỳ là ID được chọn, chuyển `is_current` thành `true`
					return {
						...point,
						is_current: point.id === id
					};
				});
			});
		},
	},
};
</script>

<style lang="scss">
	.semester-config {
		border-radius: 4px;
		background: #f8f8f8;
	}
	table.events {
		width: 100%;
		border-collapse: collapse;

		th, td {
			border: 1px solid #ddd;
			padding: 8px;
			text-align: center;
			width: auto;
			&:nth-child(3) {
				width: 75px;
			}
			&:nth-child(4) {
				width: 75px;
				@screen md {
					width: 75px;
				}
			}
		}

		th {
			background-color: #f4f4f4;
		}
	}
</style>
